import { Contact, ProjectData, Report, ReportMetadata } from '../../types';

export const DEFAULT_PREAMBLE = `1. Einführung

1.1 Vorbemerkungen
Gemäß § 3 Abs. 2 Nr. 3 der Baustellenverordnung vom 10.06.1998 ist eine Unterlage mit allen sicherheits- und gesundheitsrelevanten Angaben für mögliche spätere Arbeiten an der baulichen Anlage zusammenzustellen.

Diese Unterlage ersetzt nicht die durch Planer und Auftragnehmer erstellten Ausführungsunterlagen, Pläne, Bedienungs- und Wartungsanleitungen sowie die Objektdokumentation nach § 15 HOAI (Leistungsphase 9).

Die vorliegende Unterlage wurde nach RAB 32 erstellt und enthält sowohl die erforderlichen Angaben gemäß Abschnitt 4.2.1 als auch ergänzende Informationen gemäß Abschnitt 4.2.2.

1.2 Allgemeine Grundsätze
• Die Unterlage ist allen Unternehmen auszuhändigen, die spätere Arbeiten an der baulichen Anlage durchführen.
• Beauftragte Arbeitgeber sind selbst für Arbeitssicherheit und Gesundheitsschutz nach geltenden Vorschriften verantwortlich.
• Beschäftigte haben gemäß Unterweisung und Weisung des Arbeitgebers für ihre eigene Sicherheit Sorge zu tragen.
• Auftragnehmer müssen die Inhalte dieser Unterlage bei der Planung und Durchführung ihrer Arbeitsschutzmaßnahmen berücksichtigen.
• Die Unterlage kann zur Ausschreibung späterer Arbeiten herangezogen werden.
• Arbeitgeber, die spätere oder umbau-/änderungsbedingte Arbeiten ausführen, müssen nach §§ 5 und 6 ArbSchG eine Gefährdungsbeurteilung erstellen.
• Die UVV der zuständigen Berufsgenossenschaften, insbesondere die DGUV Vorschrift 38, sind einzuhalten.
• Arbeiten mehrerer Unternehmen im gleichen oder angrenzenden Bereich sind so zu koordinieren, dass keine gegenseitigen Gefährdungen entstehen.

1.3 Entwurfsverfasser und Projektunterlagen
Für spätere Arbeiten kann es hilfreich sein, ergänzende Informationen von Planungsbeteiligten oder ausführenden Firmen einzuholen.
Grundlage dieser Unterlage sind die von den Planungsbeteiligten bereitgestellten Unterlagen. Ergänzend wurden u. a. herangezogen:
• Lageplan, Baubeschreibung
• Zeichnungen, Revisionsunterlagen
• Technische Dokumentationen (Brandmelde-, Kälte-, Elektroanlagen)
• Wartungsunterlagen (Lüftung, Heizung, Sanitär, Brandschutz, Blitzschutz usw.)
• Betriebsanleitungen und Betriebsanweisungen

Vor Beginn jeder Arbeit sind aktuelle Unterlagen, Pläne und Beschreibungen beim Betreiber einzuholen.

1.4 Rechtliche Grundlagen und Managementsysteme
Die Durchführung von Arbeiten an der baulichen Anlage unterliegt neben der BaustellV weiteren gesetzlichen Anforderungen und Normen, die dem Schutz der Beschäftigten dienen:

• Arbeitsschutzgesetz (ArbSchG): Verpflichtet den Arbeitgeber, Gefährdungsbeurteilungen durchzuführen (§ 5) und die Beschäftigten zu unterweisen (§ 12).
• Betriebssicherheitsverordnung (BetrSichV): Regelt die Bereitstellung von Arbeitsmitteln (z. B. Gerüste, Leitern, Hubsteiger) sowie deren sichere Verwendung und Prüfung.
• Gefahrstoffverordnung (GefStoffV): Relevant bei Wartungs- und Reinigungsarbeiten unter Einsatz chemischer Stoffe.
• DIN EN ISO 45001: Diese Norm für Managementsysteme für Sicherheit und Gesundheit bei der Arbeit setzt den Rahmen für die präventive Vermeidung von Arbeitsunfällen. Auftragnehmer werden angehalten, ihre Arbeitsprozesse an den Schutzzielen dieser Norm sowie vergleichbarer Standards (z. B. SCC) auszurichten und eine kontinuierliche Verbesserung der Arbeitssicherheit anzustreben.
[umbruch]
1.5 Literatur- und Dokumentenverzeichnis
Für die Erstellung der Unterlage wurden folgende Vorschriften und Hilfsmittel verwendet:
• Landesbauordnung & Hamburgische Bauordnung
• Baustellenverordnung (BaustellV)
• Arbeitsschutzgesetz (ArbSchG)
• Betriebssicherheitsverordnung (BetrSichV)
• DIN EN ISO 45001 Managementsysteme für Sicherheit und Gesundheit
• Leitfaden der BG BAU zur Unterlagenerstellung
• BG BAU „Bausteine"
• DGUV-Vorschriften, Regeln und Informationen
• DIN 31051 Instandhaltung
• DIN 4426 Sicherheitseinrichtungen zur Instandhaltung baulicher Anlagen
• RAB 32 Unterlage für spätere Arbeiten

1.6 Gleichwertigkeitsklausel
Die beschriebenen Verfahren für Reinigung, Wartung und andere spätere Arbeiten stellen sichere Möglichkeiten dar. Gleichwertige oder sicherere Alternativen sind zulässig, sofern sie den aktuellen technischen Regeln und Arbeitsschutzvorschriften entsprechen.`;

/**
 * Anfangszustand der Projektdaten: was im Projekt gespeichert ist, sonst die
 * Stammdaten des Berichts. JEDES Feld von ProjectData, das der Autospeicher
 * zurückschreibt, muss hier übernommen werden — sonst überschreibt der erste
 * Autospeicher nach dem Öffnen den gespeicherten Wert mit dem Standard
 * (so ging die Kategoriereihenfolge verloren, Codex-Review 09.09.2026).
 */
export const getInitialProjectData = (report: Report): ProjectData => {
    const meta = report.metadata;
    const existing = report.unterlage?.projectData;
    
    const globalPreamble = report.settings?.moduleSettings?.ufsa?.defaultPreamble;
    
    return {
        projectName: existing?.projectName || meta.projectName,
        projectNumber: existing?.projectNumber || meta.projectNumber,
        location: existing?.location || meta.projectAddress,
        builder: existing?.builder || meta.clientName,
        builderAddress: existing?.builderAddress || meta.clientAddress,
        coordinator: existing?.coordinator || meta.coordinator,
        coordinatorId: existing?.coordinatorId || meta.coordinatorId,
        date: existing?.date || new Date().toISOString().split('T')[0],
        contacts: existing?.contacts || kontakteAusVerteiler(meta.distributionList),
        preamble: existing?.preamble || globalPreamble || DEFAULT_PREAMBLE,
        projectImage: existing?.projectImage || "",
        companyLogo: existing?.companyLogo || report.settings?.companyLogo || "",
        companyName: existing?.companyName || report.settings?.companyName || "",
        companyAddress: existing?.companyAddress || report.settings?.companyAddress || "",
        version: existing?.version || "1.0",
        isDraft: existing?.isDraft !== undefined ? existing.isDraft : true,
        themeColor: existing?.themeColor || "#2563eb",
        designId: existing?.designId || "classic",
        coverImageSize: existing?.coverImageSize || 'medium',
        appendixImages: existing?.appendixImages || [],
        categoryOrder: existing?.categoryOrder,
        mitgeltendeUnterlagen: existing?.mitgeltendeUnterlagen || []
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// VERALTETE KOPIE — Projektdaten gegen die gespeicherte Kopie
// ═══════════════════════════════════════════════════════════════════════════
//
// `getInitialProjectData` kopiert Bauherr und Verteiler nur beim ERSTEN
// Öffnen. Ändert sich danach der Projektverteiler, weiß die Unterlage nichts
// davon. Die Funktionen hier VERGLEICHEN nur und bauen auf Klick eine neue
// Kopie — nichts wird beim Laden überschrieben (Hausregel: Abgleich befüllt,
// überschreibt nie; die Ansicht zeigt den Hinweis, der Nutzer entscheidet).

/** Kontakte des Projektverteilers in der Form der Unterlage (Adressverzeichnis, Seite 2). */
export const kontakteAusVerteiler = (verteiler: readonly Contact[] | undefined | null): Contact[] =>
    (verteiler || []).map(c => ({
        id: c.id,
        role: c.role,
        company: c.company,
        name: c.name,
        address: c.address || '',
        phone: c.phone || '',
        email: c.email || '',
        present: c.present,
        emailDistribution: c.emailDistribution,
    }));

export type ProjektdatenFeld = 'builder' | 'builderAddress' | 'contacts';

export interface ProjektdatenAbweichung {
    feld: ProjektdatenFeld;
    titel: string;
    /** Stand in der Unterlage. */
    kopie: string;
    /** Stand in den Projektdaten. */
    projekt: string;
    /** Beim Verteiler: welche Kontakte neu bzw. geändert sind. */
    details: string[];
}

const text = (v: unknown): string => String(v ?? '').trim();

/** Felder eines Kontakts, die der Verteiler pflegt und die Unterlage spiegelt. */
const KONTAKT_FELDER: readonly (keyof Pick<Contact, 'role' | 'company' | 'name' | 'phone' | 'email'>)[] =
    ['role', 'company', 'name', 'phone', 'email'];

const kontaktTitel = (c: Contact): string =>
    [text(c.company), text(c.name)].filter(Boolean).join(' – ') || text(c.role) || c.id;

/**
 * Wo die Projektdaten heute von der gespeicherten Kopie abweichen. Leere
 * Projektdaten sind keine Abweichung — es gäbe nichts zu übernehmen. Beim
 * Verteiler zählen fehlende und geänderte Kontakte (nach Id); Kontakte, die
 * nur in der Unterlage stehen, sind kein Widerspruch.
 */
export const projektdatenAbweichungen = (
    data: ProjectData,
    meta: Partial<ReportMetadata> | null | undefined,
): ProjektdatenAbweichung[] => {
    if (!meta) return [];
    const aus: ProjektdatenAbweichung[] = [];

    const bauherr = text(meta.clientName);
    if (bauherr && bauherr !== text(data.builder)) {
        aus.push({ feld: 'builder', titel: 'Bauherr', kopie: text(data.builder) || '—', projekt: bauherr, details: [] });
    }
    const anschrift = text(meta.clientAddress);
    if (anschrift && anschrift !== text(data.builderAddress)) {
        aus.push({ feld: 'builderAddress', titel: 'Anschrift des Bauherrn', kopie: text(data.builderAddress) || '—', projekt: anschrift, details: [] });
    }

    const verteiler = meta.distributionList || [];
    if (verteiler.length) {
        const kontakte = data.contacts || [];
        const neu: string[] = [];
        const geaendert: string[] = [];
        for (const v of verteiler) {
            const k = kontakte.find(c => c.id === v.id);
            if (!k) { neu.push(kontaktTitel(v)); continue; }
            if (KONTAKT_FELDER.some(f => text(v[f]) !== text(k[f]))) geaendert.push(kontaktTitel(v));
        }
        if (neu.length || geaendert.length) {
            aus.push({
                feld: 'contacts',
                titel: 'Adressverzeichnis / Beteiligte',
                kopie: `${kontakte.length} Kontakte in der Unterlage`,
                projekt: `${verteiler.length} Kontakte im Projektverteiler`,
                details: [
                    neu.length ? `Neu im Projekt: ${neu.join(', ')}` : '',
                    geaendert.length ? `Geändert: ${geaendert.join(', ')}` : '',
                ].filter(Boolean),
            });
        }
    }
    return aus;
};

/**
 * Welche Anschrift des Bauherrn der Autospeicher der Unterlage ins Projekt
 * schreibt. Nur eine IN DER ANSICHT bearbeitete Anschrift geht zurück — dann
 * aber jede Fassung, auch die Rückkorrektur auf den alten Wert. Wurde hier
 * nichts bearbeitet, bleibt die des Projekts: die beim ersten Öffnen kopierte
 * Anschrift darf eine inzwischen im Projekt gepflegte nicht überschreiben.
 * (Ein Vergleich mit dem Öffnungswert reichte nicht: A → B → A sah aus wie
 * „nichts geändert", und im Projekt blieb B stehen.)
 */
export const anschriftFuerProjekt = (
    inAnsichtBearbeitet: boolean,
    kopie: string | undefined,
    projekt: string | undefined,
): string | undefined => (inAnsichtBearbeitet ? kopie : projekt);

/**
 * Ein Feld bzw. die Liste aus den Projektdaten übernehmen — NUR auf Klick.
 * Beim Verteiler werden Kontakte gleicher Id aktualisiert (Anschrift bleibt,
 * wenn der Verteiler keine hat), fehlende angehängt, Kontakte nur aus der
 * Unterlage behalten. Ohne Projektwert kommt dieselbe Referenz zurück.
 */
export const uebernehmeProjektdaten = (
    data: ProjectData,
    meta: Partial<ReportMetadata> | null | undefined,
    feld: ProjektdatenFeld,
): ProjectData => {
    if (!meta) return data;
    switch (feld) {
        case 'builder': {
            const wert = text(meta.clientName);
            return wert ? { ...data, builder: wert } : data;
        }
        case 'builderAddress': {
            const wert = text(meta.clientAddress);
            return wert ? { ...data, builderAddress: wert } : data;
        }
        case 'contacts': {
            const verteiler = kontakteAusVerteiler(meta.distributionList);
            if (!verteiler.length) return data;
            const bestehend = data.contacts || [];
            const aktualisiert = bestehend.map(c => {
                const v = verteiler.find(x => x.id === c.id);
                if (!v) return c;
                return {
                    ...c,
                    role: v.role,
                    company: v.company,
                    name: v.name,
                    phone: v.phone,
                    email: v.email,
                    address: v.address || c.address || '',
                    present: v.present,
                    emailDistribution: v.emailDistribution,
                };
            });
            const neue = verteiler.filter(v => !bestehend.some(c => c.id === v.id));
            return { ...data, contacts: [...aktualisiert, ...neue] };
        }
    }
};
